import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../../components/common/HtmlContent';
import useHashScroll from '../../../../hooks/useHashScroll';

/**
 * DesignStudioPage Component
 * 
 * Displays all design studio content like the legacy HTML site
 */
const DesignStudioPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Design Studio intro content from the legacy HTML
  const designStudioIntroHtml = `
    <p>
    For me, this is where true innovation can happen. Gathering stakeholders together, along with a synthesised problem in the shape of a 'Story' from our Research phase (to build context and empathy) in order to ideate and design a solution collectively.         
    </p>

    <p>
     By bringing the group together, we can generate a lot of solutions quickly, involving varying stakeholders and therefore encouraging skill and knowledge sharing - creating a harmonious highly functioning team. 
    </p>

    <p>
    Our aim is to come out of the session with a prioritised design solution, and prioritised features - ready to be prototyped and developed further.
    </p>

    <p>
    This is also where we get to test some of the tools we have already explored throughout ideation. The more we work together, the better team we will become, refining the process and therefore becoming quicker and quicker, and ultimately generating better solutions. 
    </p>
  `;

  // Templates HTML for each tool
  const participantsTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/design/ideation/design-studio/participants.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/uXjVMvt7o1U=/?share_link_id=458498030411" className="link-table">Miro</a>
    </>
  );

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            {templates && (
              <tr>
                <td>Templates</td>
                <td>{templates}</td>
              </tr>
            )}
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {/* Design Studio Intro */}
      <div id="section-tools-design-studio" className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/design">Design Tools</Link>&nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
              <Link to="/design/ideation">Ideation</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/design/ideation/design-studio">Design Studio</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={designStudioIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#the-facilitator">The Facilitator</Link></div>
            <div><Link className="tool-section-cta" to="#participants">Participants</Link></div>
            <div><Link className="tool-section-cta" to="#the-session">The Session</Link></div>
            <div><Link className="tool-section-cta" to="#research-synthesis">Research Synthesis</Link></div>
            <div><Link className="tool-section-cta" to="#ideation">Ideation</Link></div>
            <div><Link className="tool-section-cta" to="#feature-prioritisation">Feature Prioritisation</Link></div>
            <div><Link className="tool-section-cta" to="#outro">Outro</Link></div>
          </div>
        </div>
      </div>

      {/* The Facilitator Section */}
      <main className="case-study">
        <span className="case-tool-title" id="the-facilitator">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="the-facilitator">
            <span className="tool-title-number">
              <Link to="/design/ideation/design-studio/#the-facilitator">The Facilitator</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/design-studio">Design Studio</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/design-studio/#the-facilitator"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-the-facilitator" 
            id="tab-the-facilitator-overview" 
            defaultChecked
          />
          <label htmlFor="tab-the-facilitator-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Design Studio", "05/09/23")}
              <div className="tool-opening">
                <p>
                Running a Design Workshop in a structured and thought out way will help keep participants engaged, helping fuel the creativity and ultimately the solution.
                </p>
            
                <p>
                It will also speed up the session, improve knowledge sharing, and simplifying complex problems. A facilitator's role is to structure the workshop in a way that will achieve all of this and more.  
                </p>
            
                <h4>Facilitator's Role</h4>

                <p>
                So, what are the expectations, principles and goals of a Facilitator? The following list helps to summarise these.
                </p>

                <p>
                    <ul className="bullet-tk">
                        <li><b>Plan and lead activities and instruction</b> in order to help the group do their best thinking and collaborating together. Carefully consider what exercises, discussions, or teaching will allow the group to generate ideas, share perspectives, and arrive at decisions.</li>
                        <li><b>Facilitate contribution from all participants.</b> Make sure everyone has a chance to share their thoughts and ideas fully and equally. Manage dynamics to ensure no single voice dominates.</li>
                        <li><b>Maintain objectivity and neutrality.</b> As facilitator, refrain from contributing your own ideas or opinions. Your role is not to influence the outcome but to focus purely on guiding the group's process.</li>
                        <li><b>Do not dictate outcomes.</b> Allow the group to arrive at conclusions, decisions, and solutions themselves. Your role is not to impose what you think is best, but to have participants generate the best ideas and make the best choices through collaboration.</li>
                        <li><b>Guide the group to excellence.</b> Through thoughtful planning and process management, enable participants to think creatively, share openly, and decide optimally. Facilitate an environment where they can do their best collaborative thinking and decision-making.</li>
                        <li><b>Document artifacts and the entire session.</b> Remember to take notes, to log outputs, and to take pictures. These are all very helpful when it comes to the post-session development, so that they can be referred back to.</li>
                    </ul> 
                </p>
            
                <p>
                Like anything, the more you do something the better you get - it's no different with a Design Studio, so the more you observe, and practise the better a facilitator you will become - one tip would be to join other Design Studios, support another Facilitator or find someone experienced to co-facilitate with.
                </p>
                
                <h4>Day Zero Call</h4>

                <p>
                Prior to a Design Studio, a Day Zero call is a useful exercise as a means of explaining, aligning and focusing the participants. It can help iron out questions, or flag potential issues that might disrupt the productivity of the studio - we want to avoid time wasting and be as efficient as possible.
                </p>

                <p>
                    <span className="underline">Prior to Call</span><br />

                    <ul className="bullet-tk">
                        <li>Decide on call participants -  will likely be everyone who is attending the Studio.</li>
                        <li>Outline the reasoning for the studio and the end goal.</li>
                        <li>Schedule the call a few days or up to a week before the Design Studio.</li>
                    </ul>

                    <span className="underline">During the Call</span><br />

                    <ul className="bullet-tk">
                        <li>Introduce yourself and the purpose of the Design Studio, including the proposed goal.</li>
                        <li>Allow each participant to introduce themselves and to discuss any potential issues or thoughts they may have.</li>
                        <li>Agree as a group on the goal and any other important issues.</li>
                        <li>Reiterate the purpose of the Studio, the time and place and what the participants can expect.</li>
                    </ul>
                </p>    

                <p>
                Further information on a Day Zero Call can be found on <a href="https://www.nngroup.com/articles/day-0-calls/" className="link-table">Nielsen Norman Group here.</a>
                </p>

                <h4>Conducting Activities</h4>

                <p>
                Finally, as a facilitator the main job is to conduct the Design Studio activities in as productive a manner as possible - the following diagram helps visualise this process.
                </p>

                <iframe className="miro_embed" src="https://miro.com/app/embed/uXjVMvv2-94=/?pres=1&frameId=3458764561117887286&embedId=755932635434" frameBorder="0" scrolling="no" allowFullScreen></iframe>

                <p>
                Further info from the Nielson Normal Group can be found here; <br /><i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;<a href="https://www.nngroup.com/articles/ux-workshop-exercises/" className="link-table">https://www.nngroup.com/articles/ux-workshop-exercises/</a>
                </p>

                <h4>Facilitator's Toolkit</h4>
            
                <ul className="bullet-tk">
                    <li>A3 Paper</li>
                    <li>Pens</li>
                    <li>Sticky Notes</li>
                    <li>Whiteboard</li>
                    <li>Voting Dots</li>
                    <li>Sharpies</li>
                    <li>Clock</li>
                    <li>Laptop / Screen for Presentation</li>
                </ul>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/ux-workshop-agendas/" className="link-table">Planning Effective UX Workshop Agendas - Nielsen Norman Group</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/workshop-facilitation-101/" className="link-table">Workshop Facilitation 101 - Nielsen Norman Group</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Participants Section */}
      <main className="case-study">
        <span className="case-tool-title" id="participants">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="participants">
            <span className="tool-title-number">
              <Link to="/design/ideation/design-studio/#participants">Participants</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/design-studio">Design Studio</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/design-studio/#participants"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-participants" 
            id="tab-participants-overview" 
            defaultChecked
          />
          <label htmlFor="tab-participants-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Design Studio", "05/09/23", participantsTemplates)}
              <div className="tool-opening">
                <p>
                Whilst there is no reasonable limit to the type of participant, or the numbers, usually they would include;
                </p>
            
                <p>
                <b>The Facilitator</b> - the most important role, and if you're reading this, likely you. You are the Design Studio coach, bringing the stakeholders together, setting the content, direction, speed, and most importantly motivating the group to do their best work.  
                </p>
            
                <p>
                <b>Designer</b> - in terms of expertise in crafting a vision from research, a designer comes in to their own in this session. However, that doesn't necessarily mean they will have the best idea, but will be best placed to build on the better ideas. 
                </p>
            
                <p>
                <b>Researcher</b> - a vital participant, as they would have worked on the user, competitor and market research, and synthesised the findings in the lead up to the Design Studio. They will be the best placed person to clarify things and answer questions. 
                </p>    

                <p>
                <b>Developer</b> - as the person who will ultimately be creating the usable product, a developer is a valued participant because they can help manage expectations, explain constraints and estimate development time for any envisioned solution. Additionally, it's important to bring the development team along with the vision. 
                </p>
            
                <p>               
                <b>Client</b> - likely an optional participant, but obviously if the project is part of an external relationship then the main stakeholder in that is required so that they can also make sure the direction is feasible with their overall vision.  
                </p>       
                
                <p>
                <b>Subject Matter Expert</b> - particularly important if the problem and envisioned solution is specialist or beyond the understanding and knowledge of the participants. Additionally, this could also simply be a stakeholder who is particularly passionate about the problem and solving it. 
                </p>

                <p>
                It is also important to note that often these roles will overlap anyway. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/workshop-participant-lists/" className="link-table">How to Build a Participant List for UX Workshops - Nielsen Norman Group</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-participants" 
            id="tab-participants-template"
          />
          <label htmlFor="tab-participants-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVMvt7o1U=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Participants Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* The Session Section */}
      <main className="case-study">
        <span className="case-tool-title" id="the-session">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="the-session">
            <span className="tool-title-number">
              <Link to="/design/ideation/design-studio/#the-session">The Session</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/design-studio">Design Studio</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/design-studio/#the-session"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-the-session" 
            id="tab-the-session-overview" 
            defaultChecked
          />
          <label htmlFor="tab-the-session-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Design Studio", "05/09/23", (
                <>
                  <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                  <a href="/design/ideation/design-studio/design-studio-guide.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                  <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                  <a href="https://miro.com/app/embed/uXjVM3BS0Vs=/?&pres=1" className="link-table">Miro</a>
                </>
              ))}
              <div className="tool-opening">
                <h4>Presentation Guide</h4>
                <p>
                A presentation or something visual will help guide the session - reminding the participants of the point of the design studio, what is being designed for (the stories), the structure of the session, and the desired outcome, or goal. It can also be updated throughout the session as a point of reference to refer back to. 
                </p>

                <h4>Intro</h4>
                <p>
                Welcome the group, explain your role and the overarching reason for coming together for the session. And then go around the room and ask each participant to briefly introduce themselves (if they don't already know each other).  
                </p>

                <h4>Agenda & Goals</h4>
                <p>
                Explain the structure of the Session, what activities will take place and the timing of things. This is also a good opportunity to remind the participants about focus etc, and that there will be breaks so that they can check their phones, drinks, etc - but that during the session, focus is paramount. 
                </p>        
                                
                <p>
                From here we can move on to discussing the research that lead us up to this point.
                </p>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-the-session" 
            id="tab-the-session-template"
          />
          <label htmlFor="tab-the-session-template">Presentation Guide</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM3BS0Vs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="The Session Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Research Synthesis Section */}
      <main className="case-study">
        <span className="case-tool-title" id="research-synthesis">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="research-synthesis">
            <span className="tool-title-number">
              <Link to="/design/ideation/design-studio/#research-synthesis">Research Synthesis</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/design-studio">Design Studio</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/design-studio/#research-synthesis"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-research-synthesis" 
            id="tab-research-synthesis-overview" 
            defaultChecked
          />
          <label htmlFor="tab-research-synthesis-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Design Studio", "05/09/23")}
              <div className="tool-opening">
                <p>
                This is where we present our Research findings, carrying us on to HMW and ideation. From the Research Phase came the Stories (Scenarios, User/Job Stories) and now is a good time to remind the participants of those and how we arrived there. 
                </p>
            
                <p>
                This will lead us on to the Diverge/Converge technique explanation. 
                </p>

                <br />

                <h4>'The Diverge-and-Converge Technique'</h4>
                
                <p>
                We may have already explained the the purpose of the session and how the session will flow, but we can use 'The Diverge-and-Converge Technique' as a means to help us explain how the session fits in to the product creation process - remember, not all participants will be used to a workshop like this.  
                </p>
            
                <p>
                Quite simply, we have the Diverge stage, where participants will work separately in order to maximise their ideation and thoughts without interruption. And then we have the converge stage where the members come together to present and discuss their ideas and designs. 
                </p>    

                <br />

                <h4>Double Diamond</h4>

                <p>
                A core tenet of Design Thinking, the Double Diamond can be viewed from both a macro and micro level. The first Diamond is our research phase (discovering and defining the problem) and then in this instance the second 'diamond' is a visualisation of the workshop and then prototyping. When viewed as a whole, it acts a visualisation of the entire product development process over the course of its lifetime. 
                </p>

                <iframe className="miro_embed" src="https://miro.com/app/embed/uXjVMvxtDUU=/?&pres=1" frameBorder="0" scrolling="no" allowFullScreen title="Double Diamond"></iframe>

                <br />
            
                <h4>The Problem</h4>

                <p>               
                The Double Diamond would have aided us with explaining the methodology of the session and now we can remind our Participants of the original problem, that was then researched and distilled in to a Story (Job or User). We can also use our Persona as a means of building empathy amongst the group - we can also use further tools like a Storyboard - either one that was created, or it could be part of the Session if time and capacity allow.  
                </p>      
                
                <br />

                <h4>How Might We</h4>

                <p>
                Once we have covered the background of the project, including the original problem, and the user research that lead to our path forward, we can begin to create our How Might We questions. This can be done prior to the Design Studio, or with the participants as a means of finishing the research phase and beginning the ideation phase. If this is the case, time box a small period for everyone to diverge and write their HMWs and then group them by theme to cover and duplicate or similar HMWs.
                </p>

                <p>
                At this point we can introduce Dot Voting as a means of prioritising the HMWs we decide to tackle.
                </p>

                <br />

                <h4 id="dot-voting">Dot Voting</h4>

                <p>
                As simple as it sounds, Dot Voting is a fair method of choosing the best option available amongst a group in order to prioritise and move forwards.
                </p>

                <p>
                <b>1. Collect Artifacts/materials and then display</b>
                    
                    <ul className="bullet-tk">
                        <li>These could be Designs, or HMWs, or Sticky Notes or even just a list of options.</li>
                        <li>You will also need a set of Dots, with different colours for each participant. You could also weight Dots if desired.</li>
                    </ul>
                </p>

                <p>
                <b>2. Define Voting Constraints</b>
                    <ul className="bullet-tk">
                        <li>Prior to voting, communicate the purpose and significance of the voting exercise.</li>
                        <li>Specify the number of votes each participant will have; typically, about a quarter of the total options.</li>
                        <li>Optionally, participants can be restricted to voting based on areas of ownership or expertise.</li>
                    </ul>
                </p>

                <p>
                <b>3. Cast Votes</b>
                    <ul className="bullet-tk">
                        <li>Participants should cast their votes silently.</li>
                        <li>Maintain a quiet environment during the voting process, refraining from discussions.</li>
                    </ul>
                </p>

                <p>
                <b>4. Evaluate Results</b>
                    <ul className="bullet-tk">
                        <li>After all votes are cast, convene to discuss the outcomes.</li>
                        <li>The arrangement of dots resembles a heat map, highlighting popular options.</li>
                        <li>Participants can explain their voting rationale and consider next steps based on the collective ranking.</li>
                    </ul>
                </p>

                <p>
                <b>5. Consider Refinement and Re-Voting</b>
                    <ul className="bullet-tk">
                        <li>If there are tied options or the need for further prioritization, a re-vote can be conducted.</li>
                        <li>Participants receive the same number of votes but can only vote on the top options from the previous round.</li>
                    </ul>
                </p>

                <iframe className="miro_embed" src="https://miro.com/app/embed/uXjVMuxZKwI=/?&pres=1" frameBorder="0" scrolling="no" allowFullScreen title="Dot Voting"></iframe>

                <br /><br /><br />

                <h4>Parking Lot</h4>

                <p>
                I'm probably the worse for this, thoughts and ideas explode out - and not all (any) are relevant to the session. Across a group this could quickly derail the workshop. We can introduce the 'Parking Lot' at the point prior to ideation as a means of putting those thoughts to the side, that could potentially be discussed later on and revisited if they do end up becoming relevant.
                </p>
              </div>
            </div>       

            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/diverge-converge/" className="link-table">Parking Lots in UX Meetings and Workshops - Nielsen Norman Group</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/dot-voting/" className="link-table">The Diverge-and-Converge Technique for UX Workshops - Nielsen Norman Group</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-research-synthesis" 
            id="tab-research-synthesis-template"
          />
          <label htmlFor="tab-research-synthesis-template">Template</label>
          <div className="tab tab-tools">
            <b>Please Note:</b> Scroll to Page 5 for templates related to this section.
            <br /><br />
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM3BS0Vs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Research Synthesis Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Ideation Section */}
      <main className="case-study">
        <span className="case-tool-title" id="ideation">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="ideation">
            <span className="tool-title-number">
              <Link to="/design/ideation/design-studio/#ideation">Ideation</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/design-studio">Design Studio</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/design-studio/#ideation"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-ideation" 
            id="tab-ideation-overview" 
            defaultChecked
          />
          <label htmlFor="tab-ideation-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Design Studio", "05/09/23")}
              <div className="tool-opening">
                <h4>Ideation Warm Up</h4>

                <p>
                This step brings our first ideation session, the warm up - a fun start to keep things informal and help start the creativity. There are a number of Warm Up methods, but since we will be doing a lot of Sketching, a Grid warm up is a good option.
                </p>
            
                <p>
                Remember to remind the group of the Sketching process, including examples and also the Visual Alphabet and common UI Icons. 
                </p>
            
                <p>
                Once complete, ask the participants to explain their sketches/ideas as another means of getting them in the right mindset, i.e. thinking about a solution, sketching it out, and explaining it - each step encouraging simplicity and ease of understanding. 
                </p>

                <h4>Ideation</h4>
                
                <p>
                First pick a particular HMW and then using the Crazy 8s tool we can begin. Timebox the session to 5/10/15 minutes - or whatever amount of time feels right. Remember to find the right balance between fatiguing the group, but also making it as productive as possible. 
                </p>    

                <p>
                We may also include an additional HMW, or more if they are relevant and helpful to the Ideation process.  
                </p>

                <h4>Evaluation</h4>
                
                <p>               
                Now that we've had a sufficient Ideation round, participants can come together to explain their ideas and designs. Allowing for discussion amongst the group, and then Dot Voting as a means of democratically and politely deciding on the best idea and/or design.  
                </p>       
                
                <p>
                With the better ideas prioritised, we can then set about evolving them.
                </p>

                <h4>Ideation Round 2</h4>

                <p>
                Once again our participants are encouraged to work independently, but this time taking the prioritised idea or design and improving on it and evolving it - this is definitely a 'Eureka!' type moment when you see a lot of focused, smart, creative people coming together and working on a problem in an efficient way. 
                </p>

                <p>
                Following the same process as the original Ideation round, allow the team to work indepently for a time boxed period. You may also choose to introduce another HMW question at this point, particularly if it helps build on the design or ideas from the first round.
                </p>

                <h4>Evaluation Round 2</h4>

                <p>
                We can then bring the participants back together, allowing them to once again present and explain their designs to the group. We can then Dot Vote, leaving a path forward for our prospective solution. 
                </p>

                <p>
                Alternatively, we can continue to do rounds if time allows, even improving on what we've previously done, or choosing a new HMW.
                </p>

                <h4>Ideation Wrap Up</h4>

                <p>
                After this, we can wrap up the Ideation session by presenting and discussing the popular solutions. We can also make notes and archive some of the other solutions that were generated in the session using a Sketch Log.
                </p>

                <p>
                We can also potentially go back to the Parking Lot of ideas that were created if there is anything relevant worth covering. We can then move on to discussing features and wrapping up the session.
                </p>
              </div>
            </div>       

            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://designsprintkit.withgoogle.com/methodology/phase3-sketch/crazy-8s-sharing-and-voting" className="link-table">Crazy 8's Sharing and Voting - Design Sprint Kit by Google</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-ideation" 
            id="tab-ideation-template"
          />
          <label htmlFor="tab-ideation-template">Template</label>
          <div className="tab tab-tools">
            <b>Please Note:</b> Scroll to Page 7 for templates related to this section.
            <br /><br />
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM3BS0Vs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Ideation Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Feature Prioritisation Section */}
      <main className="case-study">
        <span className="case-tool-title" id="feature-prioritisation">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="feature-prioritisation">
            <span className="tool-title-number">
              <Link to="/design/ideation/design-studio/#feature-prioritisation">Feature Prioritisation</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/design-studio">Design Studio</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/design-studio/#feature-prioritisation"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-feature-prioritisation" 
            id="tab-feature-prioritisation-overview" 
            defaultChecked
          />
          <label htmlFor="tab-feature-prioritisation-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Design Studio", "05/09/23")}
              <div className="tool-opening">
                <p>
                We are in the creative mode, ideating, finding solutions and thinking about the possibilities, and as a group now is a great time to list out further features to help with the solution, and then prioritise them.
                </p>
            
                <p>
                First of all we can encourage our participants to diverge, and in a time boxed period and on separate sticky notes, write down as many relevant features as they can. This will push a fast ideation, without thinking to deeply - remember, most of the features post ideation will be obvious, or required in order for the product to function. 
                </p>
            
                <h4>Grouping</h4>

                <p>
                Because of the potentially obvious nature of some of the features, there will be a lot of crossover. We can use a basic affinity map as a means of quickly grouping and organising the features - meaning we can remove duplicates, but also organise features by functionality (or other relevant grouping) which will help when it comes to prioritising. 
                </p>
            
                <p>
                Once we have grouped the features, we can re-write them to a new sticky note ready for prioritising.  
                </p>    

                <h4>Impact vs Effort</h4>

                <p>
                Once we have a clear picture of our features, we can begin to prioritise them. As a means of speeding up this task, we can use a Impact vs Effort  Matrix to plot the features - this is a useful way of forcing the group in to really deciding if a feature will have enough impact vs the effort of creating. A developer is particularly useful in this situation. 
                </p>

                <h4>Must have Features</h4>
                
                <p>               
                We can then further prioritise so that we are ultimately choosing the best and most useful feature - particularly if resource only allows 1, 2 or 3 features. The MoSCoW tool is a good option for this, where we can decide on Must have features, Should, Could and Would have features. 
                </p>              

                <p>
                We can use Dot Voting to decide which goes where, and also a means of forcing the participants to choose between features, rather than accidentally prioritising them all as 'Must' - simply provided everyone with the number of feature notes x 4 colour dots.
                </p>

                <p>
                Depending on the size of the project or product, we can then select the features that have the most votes.
                </p>

                <p>
                Remember, we are building a quick lean product, innovating fast - the last thing we need before we've even begun building is feature bloat.
                </p>

                <p>
                If time allows, you could also go back and Ideate on the features - another bonus of bringing everyone together to come up with solutions.
                </p>
              </div>
            </div>       

            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://careerfoundry.com/en/blog/ux-design/affinity-map/" className="link-table">What is an Affinity Map? (And How to Make One) - Career Foundry</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-feature-prioritisation" 
            id="tab-feature-prioritisation-template"
          />
          <label htmlFor="tab-feature-prioritisation-template">Template</label>
          <div className="tab tab-tools">
            <b>Please Note:</b> Scroll to Page 11 for templates related to this section.
            <br /><br />
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM3BS0Vs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Feature Prioritisation Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Outro Section */}
      <main className="case-study">
        <span className="case-tool-title" id="outro">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="outro">
            <span className="tool-title-number">
              <Link to="/design/ideation/design-studio/#outro">Outro</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/design-studio">Design Studio</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/design-studio/#outro"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-outro" 
            id="tab-outro-overview" 
            defaultChecked
          />
          <label htmlFor="tab-outro-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Design Studio", "05/09/23")}
              <div className="tool-opening">
                <p>
                This is where we compile our Workshop takeaways and artifacts and make a plan to continue moving the project forward. 
                </p>

                <h4>Goals</h4>
                <p>
                We can take this opportunity to revisit the goals of the session and discuss anything that might need further clarity. 
                </p>

                <h4>Artifacts</h4>
                
                <p>
                Compiling the artifacts with the group allows for further discussion if required, these artifacts are vital for whoever is taking the project forward so that they can refer back to them. 
                </p>
                
                <p>
                    <ul className="bullet-tk">
                        <li>Pictures - documented from the session.</li>
                        <li>HMWs that the group came up with in their Prioritisation order.</li>
                        <li>Sketches - very important as this is the main point of the session, the designs and ideas that will be the basis of the product.</li>
                        <li>Features - collect all features, preserving the prioritisation order.</li>
                        <li>Parking Lot - there may have been out of scope, or off topic, or irrelevant (at this stage) conversations, points, ideas or anything. These would have been collected in the parking lot.</li>
                    </ul> 
                </p>

                <h4>Next Steps</h4>
                
                <p>
                Hopefully we have come out of the studio with a solid path forwards, group cohesion and excitement for the potential solution. Our next steps after the session would be to fill out any designs missing that will create the initial user flow (onboarding, or happy path etc), designing in low fidelity as a means of quickly creating a prototype that could be tested with real world users. 
                </p>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-outro" 
            id="tab-outro-template"
          />
          <label htmlFor="tab-outro-template">Template</label>
          <div className="tab tab-tools">
            <b>Please Note:</b> Scroll to Page 14 for templates related to this section.
            <br /><br />
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM3BS0Vs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Outro Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default DesignStudioPage; 